import React, { useState } from 'react';
import { PageHeader } from '../../components/layout/PageHeader';
import { KPICard } from '../../components/ui/KPICard';
import { Card } from '../../components/ui/Card';
import { DataTable } from '../../components/ui/DataTable';
import { Button } from '../../components/ui/Button';
import { StatusPill } from '../../components/ui/StatusPill';
import { Drawer } from '../../components/ui/Drawer';
import { Badge } from '../../components/ui/Badge';
import { PackageIcon, AlertTriangleIcon, TrendingUpIcon, ClockIcon, MessageSquareIcon, FileTextIcon } from 'lucide-react';
const forecastCommits = [{
  id: 'FC-2026-001',
  supplier: 'Oceanic Harvest Pte Ltd',
  material: 'SEA-CRB-004 (Crab)',
  forecast: '500 kg',
  committed: '400 kg',
  variance: '-20%',
  status: 'Constrained',
  dueDate: '2026-06-15',
  notes: 'Supplier reported poor catch yields due to weather conditions.'
}, {
  id: 'FC-2026-002',
  supplier: 'Golden Fields Agriculture',
  material: 'FRT-APL-001 (Apples)',
  forecast: '2000 ctn',
  committed: '2000 ctn',
  variance: '0%',
  status: 'Confirmed',
  dueDate: '2026-06-20',
  notes: 'Fully committed. Shipment scheduled for early June.'
}, {
  id: 'FC-2026-003',
  supplier: 'Pacific Meats International',
  material: 'MET-BEEF-002 (Beef)',
  forecast: '1500 kg',
  committed: 'Pending',
  variance: 'N/A',
  status: 'Pending',
  dueDate: '2026-06-25',
  notes: 'Awaiting supplier confirmation. Reminder sent.'
}, {
  id: 'FC-2026-004',
  supplier: 'Sunrise Dairy Co-op',
  material: 'DRY-MLK-005 (Fresh Milk)',
  forecast: '5000 L',
  committed: '4500 L',
  variance: '-10%',
  status: 'Constrained',
  dueDate: '2026-06-10',
  notes: 'Slight reduction due to seasonal variations.'
}, {
  id: 'FC-2026-005',
  supplier: 'Evergreen Label Manufacturing',
  material: 'PKG-BOX-012 (Cardboard Boxes)',
  forecast: '10000 pcs',
  committed: '10000 pcs',
  variance: '0%',
  status: 'Confirmed',
  dueDate: '2026-06-05',
  notes: 'Confirmed. Production started.'
}];
export const SupplyCollaborationPage: React.FC = () => {
  const [selectedCommit, setSelectedCommit] = useState<any>(null);
  return <div className="max-w-7xl mx-auto space-y-6" data-id="element-724">
      <PageHeader title="Supply Collaboration Cockpit" description="Monitor inventory exposure and forecast commits." data-id="element-725" />

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4" data-id="element-726">
        <KPICard title="Pending Commits" value="8" icon={<ClockIcon className="w-5 h-5 text-status-warning" data-id="element-728" />} data-id="element-727" />
        <KPICard title="Constrained Supply" value="3" icon={<AlertTriangleIcon className="w-5 h-5 text-status-error" data-id="element-730" />} data-id="element-729" />
        <KPICard title="Inventory Exposure" value="$2.1M" icon={<TrendingUpIcon className="w-5 h-5" data-id="element-732" />} data-id="element-731" />
        <KPICard title="Exceptions" value="5" icon={<PackageIcon className="w-5 h-5" data-id="element-734" />} data-id="element-733" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-id="element-735">
        <div className="lg:col-span-2 space-y-6" data-id="element-736">
          <Card data-id="element-737">
            <div className="flex justify-between items-center mb-4" data-id="element-738">
              <h2 className="text-lg font-semibold text-text-primary" data-id="element-739">
                Forecast Commits (Q2 2026)
              </h2>
              <Button variant="secondary" size="sm" data-id="element-740">
                Send Reminders
              </Button>
            </div>
            <DataTable data={forecastCommits} onRowClick={setSelectedCommit} columns={[{
            header: 'Supplier',
            accessorKey: 'supplier',
            cell: row => <span className="font-medium" data-id="element-742">{row.supplier}</span>
          }, {
            header: 'Material',
            accessorKey: 'material'
          }, {
            header: 'Forecast Qty',
            accessorKey: 'forecast',
            align: 'right'
          }, {
            header: 'Committed Qty',
            accessorKey: 'committed',
            align: 'right'
          }, {
            header: 'Variance',
            cell: row => <span className={row.variance.includes('-') ? 'text-status-error font-medium' : ''} data-id="element-743">
                      {row.variance}
                    </span>,
            align: 'right'
          }, {
            header: 'Status',
            cell: row => <StatusPill status={row.status} data-id="element-744" />
          }]} data-id="element-741" />
          </Card>
        </div>
        <div className="space-y-6" data-id="element-745">
          <Card className="border-l-4 border-l-status-error" data-id="element-746">
            <h2 className="text-lg font-semibold text-text-primary mb-4" data-id="element-747">
              Constrained Supply Alerts
            </h2>
            <div className="space-y-3" data-id="element-748">
              <div className="p-3 bg-surface-secondary rounded-fluent text-sm" data-id="element-749">
                <p className="font-medium text-text-primary mb-1" data-id="element-750">
                  Sri Lankan Crab Shortage
                </p>
                <p className="text-text-secondary" data-id="element-751">
                  Oceanic Harvest committed 20% below forecast for Jun 2026.
                </p>
                <Button variant="ghost" size="sm" className="mt-2 text-fpg-navy p-0" data-id="element-752">
                  Review Alternatives
                </Button>
              </div>
            </div>
          </Card>
        </div>
      </div>

      <Drawer isOpen={!!selectedCommit} onClose={() => setSelectedCommit(null)} title="Forecast Commit Details" size="md" footer={<>
            <Button variant="ghost" onClick={() => setSelectedCommit(null)} data-id="element-754">
              Close
            </Button>
            <Button variant="secondary" leftIcon={<MessageSquareIcon className="w-4 h-4" data-id="element-756" />} data-id="element-755">
              Message Supplier
            </Button>
            <Button variant="primary" data-id="element-757">Update Forecast</Button>
          </>} data-id="element-753">
        {selectedCommit && <div className="space-y-6" data-id="element-758">
            <div data-id="element-759">
              <div className="flex items-center gap-2 mb-2" data-id="element-760">
                <Badge variant="neutral" data-id="element-761">{selectedCommit.id}</Badge>
                <StatusPill status={selectedCommit.status} data-id="element-762" />
              </div>
              <h2 className="text-xl font-bold text-text-primary mb-1" data-id="element-763">
                {selectedCommit.material}
              </h2>
              <p className="text-text-secondary" data-id="element-764">{selectedCommit.supplier}</p>
            </div>

            <div className="grid grid-cols-2 gap-4" data-id="element-765">
              <div className="p-4 bg-surface-secondary rounded-fluent-lg border border-border-light" data-id="element-766">
                <p className="text-xs text-text-secondary uppercase tracking-wider font-semibold mb-1" data-id="element-767">
                  Forecast Qty
                </p>
                <p className="text-lg font-bold text-text-primary" data-id="element-768">
                  {selectedCommit.forecast}
                </p>
              </div>
              <div className="p-4 bg-surface-secondary rounded-fluent-lg border border-border-light" data-id="element-769">
                <p className="text-xs text-text-secondary uppercase tracking-wider font-semibold mb-1" data-id="element-770">
                  Committed Qty
                </p>
                <p className={`text-lg font-bold ${selectedCommit.variance.includes('-') ? 'text-status-error' : 'text-text-primary'}`} data-id="element-771">
                  {selectedCommit.committed}
                </p>
              </div>
              <div className="p-4 bg-surface-secondary rounded-fluent-lg border border-border-light" data-id="element-772">
                <p className="text-xs text-text-secondary uppercase tracking-wider font-semibold mb-1" data-id="element-773">
                  Variance
                </p>
                <p className={`text-lg font-bold ${selectedCommit.variance.includes('-') ? 'text-status-error' : 'text-text-primary'}`} data-id="element-774">
                  {selectedCommit.variance}
                </p>
              </div>
              <div className="p-4 bg-surface-secondary rounded-fluent-lg border border-border-light" data-id="element-775">
                <p className="text-xs text-text-secondary uppercase tracking-wider font-semibold mb-1" data-id="element-776">
                  Due Date
                </p>
                <p className="text-lg font-bold text-text-primary" data-id="element-777">
                  {selectedCommit.dueDate}
                </p>
              </div>
            </div>

            <div className="p-4 bg-surface-secondary rounded-fluent-lg border border-border-light" data-id="element-778">
              <h3 className="text-sm font-semibold text-text-primary mb-2 flex items-center gap-2" data-id="element-779">
                <FileTextIcon className="w-4 h-4" data-id="element-780" /> Notes
              </h3>
              <p className="text-sm text-text-secondary" data-id="element-781">
                {selectedCommit.notes}
              </p>
            </div>
          </div>}
      </Drawer>
    </div>;
};